// Players search (ADMIN_UI §3.7). One input box accepts:
//  - internal id (numeric)         → straight nav to /players/:id
//  - app:external_id (sg:12345)    → lookupPlayers({app, external_id})
//  - email (contains @)            → lookupPlayers({email})
// Renders a result list per match; click → /players/:id.

import { useMutation } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { lookupPlayers } from '../../lib/api/players.js';
import type { PlayerRecord } from '../../lib/api/types.js';
import { Button } from '../../components/ui/button.js';
import { Input } from '../../components/ui/input.js';
import { Badge } from '../../components/ui/badge.js';

export const Route = createFileRoute('/_authed/players/')({
  component: PlayersSearchPage,
});

function parseQuery(
  q: string,
):
  | { kind: 'id'; id: string }
  | { kind: 'lookup'; params: Parameters<typeof lookupPlayers>[0] }
  | { kind: 'invalid' } {
  const trimmed = q.trim();
  if (!trimmed) return { kind: 'invalid' };
  if (/^[0-9]+$/.test(trimmed)) return { kind: 'id', id: trimmed };
  if (trimmed.includes('@')) return { kind: 'lookup', params: { email: trimmed } };
  const match = /^([a-z]+):(.+)$/.exec(trimmed);
  if (match) return { kind: 'lookup', params: { app: match[1]!, external_id: match[2]! } };
  return { kind: 'invalid' };
}

function PlayersSearchPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<PlayerRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (params: Parameters<typeof lookupPlayers>[0]) => lookupPlayers(params),
    onSuccess: (data) => {
      setResults(data.players);
      setError(null);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Lookup failed');
      setResults(null);
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = parseQuery(q);
    if (parsed.kind === 'invalid') {
      setError(
        'Enter an internal player ID, an app:external_id pair (e.g. sg:12345), or an email.',
      );
      setResults(null);
      return;
    }
    if (parsed.kind === 'id') {
      void navigate({ to: '/players/$playerId', params: { playerId: parsed.id } });
      return;
    }
    setError(null);
    mutation.mutate(parsed.params);
  }

  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold text-neutral-900">Players</h2>

      <form onSubmit={onSubmit} className="mb-6 flex max-w-2xl gap-2">
        <Input
          aria-label="Search players"
          placeholder="Internal id, sg:12345, or email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Searching…' : 'Search'}
        </Button>
      </form>

      {error && (
        <p role="alert" className="mb-4 text-sm text-red-600">
          {error}
        </p>
      )}

      {results === null && !error && (
        <p className="text-sm text-neutral-500">
          Search for a player by ID, app + external ID, or email.
        </p>
      )}

      {results !== null && results.length === 0 && (
        <p className="text-sm text-neutral-500">No players matched.</p>
      )}

      {results && results.length > 0 && (
        <ul className="divide-y divide-neutral-200 rounded-md border border-neutral-200 bg-white">
          {results.map((p) => (
            <li key={p.id} className="p-4">
              <Link
                to="/players/$playerId"
                params={{ playerId: p.id }}
                className="flex items-baseline gap-3 hover:underline"
              >
                <Badge variant="neutral">{p.app}</Badge>
                <span className="font-medium text-neutral-900">{p.email ?? '(no email)'}</span>
                <span className="font-mono text-xs text-neutral-500">{p.external_id}</span>
                <span className="ml-auto text-xs text-neutral-500">id:{p.id}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
